import axios from "axios";
import { appConfig } from "@/assets/scripts/js/AppConfig";
import { MockApiService } from "./mock/MockApiService";

export class ApiClient {
  constructor() {
    this.isMock = appConfig.APP_CONFIG.MOCK_MODE;

    if (this.isMock) {
      this.client = new MockApiService();
      this.initMockInterceptors();
    } else {
      this.client = axios.create({
        baseURL: appConfig.APP_CONFIG.BASE_URL,
        headers: { "Content-Type": "application/json" },
        timeout: 5000,
      });

      this.initAxiosInterceptors();
    }
  }

  initAxiosInterceptors() {
    // リクエスト interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // add retry config
        config.retryCount = 0;
        config.retryLimit = 3;

        return config;
      },
      (error) => Promise.reject(error)
    );

    // レスポンスとリトライ interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error.config;

        // Mockモードならリトライしない
        if (!config || this.isMock) return Promise.reject(error);

        // 3回リトライ
        if (config.retryCount >= config.retryLimit) {
          return Promise.reject(error);
        }

        // 以下のエラーはリトライ対象
        const shouldRetry =
          error.code === "ECONNABORTED" || // タイムアウト
          !error.response || // ネットワークエラー
          (error.response.status >= 500 && error.response.status < 600); // server error

        if (!shouldRetry) {
          return Promise.reject(error);
        }

        config.retryCount++;

        // リトライ前に待機（指数バックオフ）
        const delay = 200 * Math.pow(2, config.retryCount - 1);
        await new Promise((resolve) => setTimeout(resolve, delay));

        // リトライ
        return this.client(config);
      }
    );
  }

  initMockInterceptors() {
    this.mockInterceptors = {
      request: [],
      response: [],
      error: [],
    };

    this.useMockRequest((method, url, body) => {
      return { method, url, body };
    });

    this.useMockResponse((res) => {
      return res;
    });

    this.useMockError((error, context) => {
      console.error("Mock API Error:", error, context);

      return Promise.reject(error);
    });
  }

  // axios.interceptors.request.useをモック
  useMockRequest(handler) {
    this.mockInterceptors.request.push(handler);
  }

  // axios.interceptors.response.useをモック
  useMockResponse(handler) {
    this.mockInterceptors.response.push(handler);
  }

  // axios.interceptors.response.use(_, onError)をモック
  useMockError(handler) {
    this.mockInterceptors.error.push(handler);
  }

  delay(ms = 200) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async request(method, url, body = null, config = {}) {
    if (this.isMock) {
      await this.delay();

      let finalUrl = url;
      if (config.params) {
        const qs = new URLSearchParams(config.params).toString();
        finalUrl = qs ? `${url}?${qs}` : url;
      }

      let req = { method, url: finalUrl, body };
      this.mockInterceptors.request.forEach((fn) => {
        req = fn(req.method, req.url, req.body) || req;
      });

      try {
        let resp = await this.client[method](req.url, req.body);

        this.mockInterceptors.response.forEach((fn) => {
          resp = fn(resp) || resp;
        });

        return resp;
      } catch (error) {
        for (const fn of this.mockInterceptors.error) {
          await fn(error, req);
        }
        throw error;
      }
    }

    try {
      const response = await this.client.request({
        method,
        url,
        data: body,
        params: config.params,
      });
      return response.data;
    } catch (err) {
      if (err.response) {
        throw new Error(err.response.data || err.response.statusText);
      }
      if (err.request) {
        throw new Error("No response from server");
      }
      throw err;
    }
  }

  get(url, config = {}) {
    return this.request("get", url, null, config);
  }
  post(url, b) {
    return this.request("post", url, b);
  }
  put(url, b) {
    return this.request("put", url, b);
  }
  delete(url) {
    return this.request("delete", url);
  }
}
